import { ENV } from '@/constants';
import { exploreSchemaItemList } from '../exploreSchema';

export const schemaObject_ColorPickerFromImage = {
	'@context': 'https://schema.org',
	'@graph': [
		{
			'@type': 'WebPage',
			'@id': 'https://liftapp.ai/color-picker-from-image',
			name: 'Color Picker from Image: Get HEX, RGB & HSL by Lift',
			description:
				'Upload any photo and identify exact colors with a live eyedropper — get HEX, RGB, and HSL codes for any pixel, plus an auto-generated image color palette. Try Lift today!',
			url: 'https://liftapp.ai/color-picker-from-image',
			image: 'https://liftapp.ai/seo-image.webp',
			mainEntity: [
				{ '@id': 'https://liftapp.ai/color-picker-from-image#product' },
				{ '@id': 'https://liftapp.ai/color-picker-from-image#howto' },
				{ '@id': 'https://liftapp.ai/color-picker-from-image#benefits' },
				{ '@id': 'https://liftapp.ai/color-picker-from-image#faq' },
				{ '@id': 'https://liftapp.ai/color-picker-from-image#why-lift' },
				{ '@id': 'https://liftapp.ai/color-picker-from-image#explore-tools' },
				{ '@id': 'https://liftapp.ai/color-picker-from-image#all-in-one' },
			],
		},
		{
			'@type': 'Product',
			'@id': 'https://liftapp.ai/color-picker-from-image#product',
			name: 'Color Picker from Image',
			description:
				'Upload a photo, hover the eyedropper over any spot, and get the exact color in HEX, RGB, or HSL in real time. Your image also generates a full palette of its dominant colors. Try Lift today.',
			url: 'https://liftapp.ai/color-picker-from-image',
			fileFormat: ['image/jpeg', 'image/png', 'image/webp'],
			memoryRequirements: 'Max file size: 20MB',
			brand: {
				'@type': 'Brand',
				name: 'Lift',
			},
			aggregateRating: {
				'@type': 'AggregateRating',
				ratingValue: '4.8',
				reviewCount: 130000,
				bestRating: '5',
				worstRating: '1',
			},
			interactionStatistic: [
				{
					'@type': 'InteractionCounter',
					interactionType: { '@type': 'InstallAction' },
					userInteractionCount: 13000000,
				},
			],
		},
		{
			'@type': 'HowTo',
			'@id': 'https://liftapp.ai/color-picker-from-image#howto',
			name: 'How it works',
			step: [
				{
					'@type': 'HowToStep',
					position: 1,
					name: 'Upload your photo',
					text: 'Add any image to the editor and the eyedropper activates automatically on the canvas.',
				},
				{
					'@type': 'HowToStep',
					position: 2,
					name: 'Hover to identify any color',
					text: 'The live eyedropper tracks every pixel and shows a real-time color preview as you move across the image.',
				},
				{
					'@type': 'HowToStep',
					position: 3,
					name: 'Click to lock your selection',
					text: 'Tap any spot to fix the color pick; HEX, RGB, and HSL values load instantly in the side panel.',
				},
				{
					'@type': 'HowToStep',
					position: 4,
					name: 'Copy and explore the palette',
					text: "Copy any color format with one click and browse the auto-generated palette of your image's dominant colors.",
				},
			],
		},
		{
			'@type': 'ItemList',
			'@id': 'https://liftapp.ai/color-picker-from-image#benefits',
			name: 'Who can benefit from the Lift Color Picker?',
			description:
				'Lift Color Picker helps graphic designers, content creators, developers, e-commerce sellers, and interior design enthusiasts identify exact colors from any image with HEX, RGB, and HSL codes.',
			itemListOrder: 'Unordered',
			itemListElement: [
				{
					'@type': 'ListItem',
					position: 1,
					name: 'Graphic designers & brand teams — Identify exact colors from reference images without guesswork. Lift returns precise HEX codes ready to paste into any design tool.',
				},
				{
					'@type': 'ListItem',
					position: 2,
					name: 'Content creators & social media managers — Build a cohesive visual identity by matching post colors to a signature photo. Extract dominant tones from any image in seconds.',
				},
				{
					'@type': 'ListItem',
					position: 3,
					name: 'Web & app developers — Get pixel-accurate HEX and RGB codes from mockups or screenshots to use directly in CSS. Lift delivers the exact values without switching between tools.',
				},
				{
					'@type': 'ListItem',
					position: 4,
					name: 'E-commerce sellers — Identify exact product shades to describe colors accurately in listings and ad creatives. The eyedropper reads any tone straight from a product photo.',
				},
				{
					'@type': 'ListItem',
					position: 5,
					name: 'Interior design enthusiasts — Pull specific colors from room inspiration photos to match paint, fabric, or décor. The image palette highlights the dominant tones at a glance.',
				},
			],
			image: {
				'@type': 'ImageObject',
				contentUrl:
					'https://liftapp.ai/_next/static/media/benefits.3da6de53.webp',
				name: 'Lift color picker from image — lavender field with purple HEX code and five-tone extracted palette',
				description:
					'Color picker from image on a lavender field showing HEX code #7B5EA7 and an extracted purple-beige palette',
			},
		},
		{
			'@type': 'FAQPage',
			'@id': 'https://liftapp.ai/color-picker-from-image#faq',
			mainEntity: [
				{
					'@type': 'Question',
					name: 'What is the Lift Color Picker?',
					acceptedAnswer: {
						'@type': 'Answer',
						text: "The Lift Color Picker is an image-based color detection tool that identifies any color in your photo using a live eyedropper. It returns the exact code in HEX, RGB, and HSL, and auto-generates a palette of your image's dominant colors.",
					},
				},
				{
					'@type': 'Question',
					name: 'How does the Lift Color Picker work?',
					acceptedAnswer: {
						'@type': 'Answer',
						text: 'Upload a photo and hover the eyedropper over any area to preview the color in real time. Click to lock your selection and instantly view HEX, RGB, and HSL values in the side panel.',
					},
				},
				{
					'@type': 'Question',
					name: 'Can I get the color code in multiple formats at once?',
					acceptedAnswer: {
						'@type': 'Answer',
						text: 'Yes — every color you pick is displayed in HEX, RGB, and HSL simultaneously in the panel. Each format has its own copy button, so you grab exactly the value your workflow needs.',
					},
				},
				{
					'@type': 'Question',
					name: 'What image formats does the Lift Color Picker support?',
					acceptedAnswer: {
						'@type': 'Answer',
						text: 'You can upload JPG, PNG, WEBP, and HEIC files in any size or resolution. The tool works equally well with product shots, screenshots, landscapes, and scanned artwork.',
					},
				},
			],
		},
		{
			'@type': 'ItemList',
			'@id': 'https://liftapp.ai/color-picker-from-image#why-lift',
			name: 'Why Lift Color Picker',
			description:
				'High-quality results, intelligent rendering, cross-platform support, and budget-friendly pricing.',
			itemListOrder: 'Unordered',
			itemListElement: [
				{
					'@type': 'ListItem',
					position: 1,
					name: 'High-quality results',
					description:
						'AI-powered technology delivers professional-grade visuals every time',
				},
				{
					'@type': 'ListItem',
					position: 2,
					name: 'Intelligent rendering',
					description:
						'AI tailors the effect to the scene and subject for optimal results',
				},
				{
					'@type': 'ListItem',
					position: 3,
					name: 'Cross-platform support',
					description: 'Available on iOS, Android, and Web for seamless access',
				},
				{
					'@type': 'ListItem',
					position: 4,
					name: 'Budget-friendly',
					description:
						'Save on costly designers with an affordable and intuitive tool',
				},
			],
			image: {
				'@type': 'ImageObject',
				contentUrl:
					'https://liftapp.ai/_next/static/media/whyUse.ae83a89c.webp',
				name: 'Lift color picker from image on mobile — desert sand dunes with HEX color swatches and auto palette',
				description:
					'Mobile phone showing Lift color picker on a sand dunes photo with HEX code #C1440E and floating color chips',
			},
		},
		{
			'@type': 'ItemList',
			'@id': 'https://liftapp.ai/color-picker-from-image#explore-tools',
			name: 'Explore the AI tools',
			description:
				'A curated list of AI-powered tools for image generation, enhancement, background removal, and more – all available on Lift.',
			itemListOrder: 'Unordered',
			...exploreSchemaItemList,
		},
		{
			'@type': 'SoftwareApplication',
			'@id': 'https://liftapp.ai/color-picker-from-image#all-in-one',
			name: 'Try Lift – AI Content Creation Tools',
			description:
				'Your all-in-one tools to create, edit, and elevate your content. Create stunning content with AI tools, templates, and editing features effortlessly.',
			operatingSystem: 'iOS, Android, Web',
			applicationCategory: 'MultimediaApplication',
			url: `${ENV.LIFT_WEB_APP_URL}/sign-in?auto_login=true`,
			applicationSubCategory: 'Content Creation',
			publisher: {
				'@type': 'Organization',
				name: 'Lift',
				url: 'https://liftapp.ai',
			},
			potentialAction: [
				{
					'@type': 'ViewAction',
					name: 'Open Web App',
					target: `${ENV.LIFT_WEB_APP_URL}/sign-in?auto_login=true`,
				},
			],
		},
		{
			'@type': 'BreadcrumbList',
			itemListElement: [
				{
					'@type': 'ListItem',
					position: 1,
					name: 'Home',
					item: 'https://liftapp.ai',
				},
				{
					'@type': 'ListItem',
					position: 2,
					name: 'Color Picker from Image',
					item: 'https://liftapp.ai/color-picker-from-image',
				},
			],
		},
	],
};
